import { createContext, type ReactNode, use, useCallback, useMemo } from "react";

import { useRecordSearchParams, useRecordWriteParams } from "~/contexts/RecordLocationContext";

/** Inner-strip selection, keyed by compose slot so sibling strips do not share an index. */
export const TAB_SLOT_PARAM_PREFIX = "t.";

const parseTabPath = (value: string | null): string[] =>
  (value ?? "").split("/").filter((segment) => segment.length > 0);

export const slotSelectionsFromSearch = (params: URLSearchParams): Record<string, string> => {
  const next: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    if (key.startsWith(TAB_SLOT_PARAM_PREFIX) && value.length > 0) {
      next[key.slice(TAB_SLOT_PARAM_PREFIX.length)] = value;
    }
  }
  return next;
};

const TabPathContext = createContext<{
  segments: string[];
  bySlot: Record<string, string>;
  select: (depth: number, key: string, replace: boolean, slotKey: string) => void;
} | null>(null);

const TabPathDepthContext = createContext(0);

export const TabPathProvider = ({
  children,
  baseDepth = 0,
  pinnedBySlot,
  onSelectInner,
}: {
  children: ReactNode;
  /** Segment index the first inner strip on this surface writes. */
  baseDepth?: number;
  /** Composer selection wins over the URL so the selected inner tab stays open. */
  pinnedBySlot?: Record<string, string>;
  /** User click on an inner tab, after the URL write. Auto-corrections do not call this. */
  onSelectInner?: (slotKey: string, tabKey: string) => void;
}) => {
  const [searchParams, setSearchParams] = useRecordSearchParams();
  const writeParams = useRecordWriteParams();
  const tabParam = searchParams.get("tab");
  // One array per `?tab=` value: `select` and the context value are memoised on it.
  const segments = useMemo(() => parseTabPath(tabParam), [tabParam]);
  const fromSearch = useMemo(() => slotSelectionsFromSearch(searchParams), [searchParams]);
  const bySlot = useMemo(() => ({ ...fromSearch, ...pinnedBySlot }), [fromSearch, pinnedBySlot]);

  const select = useCallback(
    (_depth: number, key: string, replace: boolean, slotKey: string) => {
      const params = writeParams();
      params.set(`${TAB_SLOT_PARAM_PREFIX}${slotKey}`, key);
      if (replace) {
        const record = segments[0];
        if (record) {
          params.set("tab", record);
        } else {
          params.delete("tab");
        }
      }
      setSearchParams(params, { replace });
      if (!replace) {
        onSelectInner?.(slotKey, key);
      }
    },
    [onSelectInner, segments, setSearchParams, writeParams],
  );

  const value = useMemo(() => ({ segments, bySlot, select }), [bySlot, segments, select]);

  return (
    <TabPathContext value={value}>
      <TabPathDepthContext value={baseDepth}>{children}</TabPathDepthContext>
    </TabPathContext>
  );
};

export const TabPathDepthProvider = ({
  children,
  depth,
}: {
  children: ReactNode;
  depth: number;
}) => <TabPathDepthContext value={depth}>{children}</TabPathDepthContext>;

export const useTabPath = (): {
  segments: string[];
  bySlot: Record<string, string>;
  depth: number;
  select: (depth: number, key: string, replace: boolean, slotKey: string) => void;
} => {
  const path = use(TabPathContext);
  const depth = use(TabPathDepthContext);
  if (!path) {
    return {
      segments: [],
      bySlot: {},
      depth,
      select: () => undefined,
    };
  }

  return { segments: path.segments, bySlot: path.bySlot, depth, select: path.select };
};
